import { useRef, useState, type ReactNode } from 'react';
import Slider from 'react-slick';
import RawHtml from 'utils/miscUtils/rawHtml';
import type { MediaItem } from 'interfaces/content/Media';
import { NextCustomImage } from 'components/Image';
import NextArrow from 'components/Arrows/NextArrow';
import PrevArrow from 'components/Arrows/PrevArrow';
import useBreakpoint from 'hooks/useBreakpoint';
import VodVideo from 'components/VodVideo';
import type { CompanyRankingList } from 'api/fragments';
import { clearHTMLString } from 'utils/stringUtils';
import getCompanyPageTestId from 'constants/testsIds/companyPage';
import { parseCaptionWhenAltTextEmpty, parseContentForEmptySpace } from 'utils/parsingUtils/clientSideParsing';
import Accordion from 'components/Globals/Accordion';
import Tabs from 'components/Globals/Tabs';
import { addDots } from 'components/Globals/Dots';
import { showRankValue } from 'utils/objectUtils';
import RankWithLabel from 'components/Globals/RankWithLabel';
import EarningsReportsList, { type EarningsList } from 'components/Company/EarningsReportsList';
import GreatPlaceToWorkItem from './GreatPlaceItem';
import ChangeTheWorldItem from './ChangeTheWorldItem';

import {
  AboutWrapper,
  DescriptionWrapper,
  GreatPlaceWrapper,
  SliderWrapper,
  Credit,
  FeaturedMedia,
} from './AboutCompanyStyles';

interface AccordionCompanyItem {
  title: string;
  className: string;
  children: ReactNode;
  defaultExpanded?: boolean;
}

export interface AboutCompanyProps {
  description: string;
  aboutFeaturedImage: MediaItem | null;
  gptwProfileLink: string;
  greatPlaceFeaturedData?: CompanyRankingList[] | null;
  changeTheWorldData?: CompanyRankingList[] | null;
  aboutFeaturedVideo?: string;
  greatPlaceFeaturedVideo?: string;
  companyTitle: string;
  tabDataCy?: string;
  contentDataCy?: string;
  rankings?: CompanyRankingList[];
  earningsReports?: EarningsList[];
}
const AboutCompany = ({
  description,
  aboutFeaturedImage,
  aboutFeaturedVideo,
  gptwProfileLink,
  greatPlaceFeaturedData,
  changeTheWorldData,
  greatPlaceFeaturedVideo,
  tabDataCy,
  contentDataCy,
  rankings,
  companyTitle,
  earningsReports,
}: AboutCompanyProps) => {
  const isMobile = useBreakpoint('mdMax');

  const initialSliderSettings = {
    appendDots: addDots,
    dots: true,
    dotsClass: 'slick-dots',
    draggable: true,
    infinite: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1152,
        settings: {
          arrows: false,
        },
      },
    ],
    slidesToScroll: 1,
    slidesToShow: 1,
    speed: 500,
    swipeToSlide: true,
  };

  const [sliderSettings, setSliderSettings] = useState(initialSliderSettings);
  const sliderRef = useRef<Slider | null>(null);

  const setEnableSlide = (isEnabled: boolean) => {
    if (sliderRef.current) {
      setSliderSettings({ ...sliderSettings, draggable: isEnabled });
    }
  };

  let aboutFeaturedItem;
  if (aboutFeaturedVideo) {
    aboutFeaturedItem = (
      <FeaturedMedia>
        <VodVideo videoId={aboutFeaturedVideo} />
      </FeaturedMedia>
    );
  } else if (aboutFeaturedImage?.mediaItemUrl) {
    aboutFeaturedItem = (
      <FeaturedMedia
        className='about-featured'
        data-cy={getCompanyPageTestId('ABOUT_FEATURED')}
      >
        <NextCustomImage
          src={aboutFeaturedImage.mediaItemUrl}
          alt={parseCaptionWhenAltTextEmpty(aboutFeaturedImage)}
          width='975'
          height='650'
        />
        <Credit data-cy={getCompanyPageTestId('ABOUT_CREDIT')}>{aboutFeaturedImage.credit}</Credit>
      </FeaturedMedia>
    );
  } else {
    aboutFeaturedItem = null;
  }

  const aboutChildren = clearHTMLString(description) ? (
    <AboutWrapper>
      <DescriptionWrapper data-cy={getCompanyPageTestId('ABOUT_DESCRIPTION')}>
        {RawHtml({ html: parseContentForEmptySpace(description) })}
      </DescriptionWrapper>
      {aboutFeaturedItem}
    </AboutWrapper>
  ) : null;

  const greatPlaceToWorkChildren =
    greatPlaceFeaturedData && greatPlaceFeaturedData.length > 0 ? (
      <GreatPlaceWrapper>
        <SliderWrapper>
          <Slider
            ref={sliderRef}
            {...sliderSettings}
          >
            {greatPlaceFeaturedData.map((data) => (
              <GreatPlaceToWorkItem
                key={`${data.title}-${data.year}`}
                text={data.itemDescription}
                edition={`${data.title} ${data.year} `}
                link={gptwProfileLink}
                video={greatPlaceFeaturedVideo}
                image={typeof data.image !== 'string' ? data.image : null}
                setEnableSlide={setEnableSlide}
              />
            ))}
          </Slider>
        </SliderWrapper>
      </GreatPlaceWrapper>
    ) : null;

  const changeTheWorldChildren =
    changeTheWorldData && changeTheWorldData.length > 0 ? (
      <div>
        {changeTheWorldData.map((data) => (
          <ChangeTheWorldItem
            key={`${data.title}-${data.year}`}
            text={data.itemDescription}
            edition={`${data.title} ${data.year} `}
            image={typeof data.image !== 'string' ? data.image : null}
          />
        ))}
      </div>
    ) : null;

  const tabsConfig = [];

  if (aboutChildren) {
    tabsConfig.push({
      children: aboutChildren,
      title: 'About',
    });
  }

  if (greatPlaceToWorkChildren) {
    tabsConfig.push({
      children: greatPlaceToWorkChildren,
      title: 'Great Place to Work',
    });
  }

  if (changeTheWorldChildren) {
    tabsConfig.push({
      children: changeTheWorldChildren,
      title: 'Change The World',
    });
  }

  if (earningsReports?.length) {
    tabsConfig.push({
      children: <EarningsReportsList reports={earningsReports} />,
      title: 'Earnings Reports',
    });
  }

  const hasAboutData = !!aboutChildren;
  const hasGreatPlacesData = !!greatPlaceToWorkChildren;
  const hasChangeTheWorldData = !!changeTheWorldChildren;
  const hasRankings = rankings?.length;

  const firstRankItem = hasRankings && (
    <RankWithLabel
      rank={rankings[0].rank}
      name={rankings[0].title}
      uri={rankings[0].uri}
      showRank={showRankValue(rankings[0])}
      companyName={companyTitle}
    />
  );

  const rankingList =
    hasRankings &&
    rankings.slice(1).map(
      (ranking) =>
        ranking.rank <= 3000 && (
          <RankWithLabel
            rank={ranking.rank}
            name={ranking.title}
            key={ranking.slug}
            uri={ranking.uri}
            showRank={showRankValue(ranking)}
            companyName={companyTitle}
          />
        ),
    );

  const accordionItems: AccordionCompanyItem[] = [
    {
      children: hasRankings && rankings?.length > 1 && rankingList,
      className: 'ranking-list',
      title: `${companyTitle} in Fortune Rankings`,
    },
    {
      children: hasAboutData && aboutChildren,
      className: '',
      defaultExpanded: true,
      title: 'About',
    },
    {
      children: hasGreatPlacesData && greatPlaceToWorkChildren,
      className: '',
      defaultExpanded: true,
      title: 'Great Place to Work',
    },
    {
      children: hasChangeTheWorldData && changeTheWorldChildren,
      className: '',
      defaultExpanded: true,
      title: 'Change the World',
    },
  ];

  if (earningsReports?.length) {
    accordionItems.push({
      children: <EarningsReportsList reports={earningsReports} />,
      className: '',
      defaultExpanded: false,
      title: 'Reports',
    });
  }

  const initialExpandedSections = [];

  for (let i = 0; i < accordionItems.length; i += 1) {
    initialExpandedSections.push(!!accordionItems[i].defaultExpanded);
  }

  const [expandedSections, setExpandedSections] = useState<boolean[]>(initialExpandedSections);

  const toggleSections = (index: number) => {
    setExpandedSections((prev) => {
      const updatedSections = [...prev];
      updatedSections[index] = !updatedSections[index];
      return updatedSections;
    });
  };
  const finalAccordionItems = accordionItems.filter((item) => !!item.children);

  const mobileAccordion = (
    <div className='accordions'>
      {firstRankItem}
      {(hasAboutData || hasGreatPlacesData) &&
        finalAccordionItems.map((item, index) => (
          <Accordion
            index={index}
            key={item.title}
            title={item.title}
            isExpanded={expandedSections[index]}
            onClick={() => toggleSections(index)}
            className={item.className}
            dataCyToggle={tabDataCy}
          >
            {item.children}
          </Accordion>
        ))}
    </div>
  );

  return isMobile ? (
    mobileAccordion
  ) : (
    <Tabs
      config={tabsConfig}
      tabDataCy={tabDataCy}
      contentDataCy={contentDataCy}
      defaultIndex={0}
      variant='default'
      className='tabs'
    />
  );
};

export default AboutCompany;
